// src/pages/AiTransparency.jsx
// Page publique de transparence IA (AI Act art. 50 + art. 4). Accessible depuis le footer
// et depuis chaque <AiDisclosure> (« En savoir plus »). Contenu : ARTKIV_AI_ACT_COMPLIANCE_2026 §5.
import React from "react";
import { useTranslation } from "react-i18next";

const INK = "#1a1a1a";
const MUTED = "#4a453d";
const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

export default function AiTransparency() {
  const { t } = useTranslation();

  const Section = ({ title, children }) => (
    <section style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: INK, margin: "0 0 10px", fontFamily: "Georgia, serif" }}>{title}</h2>
      <div style={{ fontSize: 15, lineHeight: 1.7, color: MUTED }}>{children}</div>
    </section>
  );

  return (
    <div style={{ background: "#f9f7f4", minHeight: "100vh", fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 24px 80px" }}>
        <div style={{ fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", color: GOLD, marginBottom: 10 }}>ARTKIV</div>
        <h1 style={{ fontSize: 34, fontWeight: 700, color: INK, margin: "0 0 12px", lineHeight: 1.2 }}>
          {t("aiTransparency.title", { defaultValue: "Comment ARTKIV utilise l'intelligence artificielle" })}
        </h1>
        <div style={{ height: 1, width: 48, background: GOLD, margin: "0 0 8px" }} />
        <p style={{ fontSize: 15, lineHeight: 1.7, color: MUTED, margin: 0 }}>
          {t("aiTransparency.intro", {
            defaultValue:
              "ARTKIV utilise des systèmes d'intelligence artificielle pour assister certaines tâches de gestion et d'analyse des collections : extraction de données, classification, analyse visuelle et aide à l'analyse. L'IA propose, vous comprenez, vous vérifiez et validez ; ARTKIV trace.",
          })}
        </p>

        <Section title={t("aiTransparency.notReplace.h", { defaultValue: "L'IA ne remplace pas l'expertise humaine" })}>
          {t("aiTransparency.notReplace.p", {
            defaultValue:
              "Les résultats générés ou proposés automatiquement peuvent contenir des erreurs. ARTKIV distingue clairement les résultats automatisés des informations revues ou validées par un utilisateur ou un professionnel, au moyen de mentions visibles au moment où l'IA intervient.",
          })}
        </Section>

        <Section title={t("aiTransparency.states.h", { defaultValue: "Quatre états, jamais confondus" })}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>{t("aiTransparency.states.generated", { defaultValue: "Généré par IA — un résultat produit par une IA." })}</li>
            <li>{t("aiTransparency.states.extracted", { defaultValue: "Extrait automatiquement — une information lue par OCR, à vérifier." })}</li>
            <li>{t("aiTransparency.states.reviewed", { defaultValue: "Revu par l'utilisateur — un résultat IA relu par une personne." })}</li>
            <li>{t("aiTransparency.states.expert", { defaultValue: "Validé par un expert — un résultat validé dans un workflow d'expertise." })}</li>
          </ul>
        </Section>

        <Section title={t("aiTransparency.sensitive.h", { defaultValue: "Décisions sensibles" })}>
          {t("aiTransparency.sensitive.p", {
            defaultValue:
              "Les fonctions IA d'ARTKIV ne doivent pas être utilisées seules pour prendre une décision juridique, fiscale, patrimoniale, d'authentification, de conservation ou d'assurance. Une valeur ou une conclusion destinée à un usage professionnel suit un workflow de validation humaine.",
          })}
        </Section>

        <Section title={t("aiTransparency.data.h", { defaultValue: "Données" })}>
          {t("aiTransparency.data.p", {
            defaultValue:
              "Avant tout appel à une fonction IA, ARTKIV vérifie votre authentification et vos droits d'accès à l'œuvre ou à la collection concernée, et ne transmet au prestataire que les données nécessaires à la tâche. Le droit d'utiliser un module IA n'ouvre jamais un accès supplémentaire à vos données. Certaines fonctions font appel à des prestataires d'IA tiers ; les catégories de données traitées, les mesures de sécurité et les conditions de traitement sont décrites dans notre politique de confidentialité.",
          })}
        </Section>

        <Section title={t("aiTransparency.contact.h", { defaultValue: "Nous contacter" })}>
          {t("aiTransparency.contact.p", { defaultValue: "Pour toute question sur l'usage de l'IA chez ARTKIV : " })}
          <a href="mailto:legal@artkiv.com" style={{ color: GOLD, textDecoration: "underline" }}>legal@artkiv.com</a>
        </Section>

        <p style={{ marginTop: 40, paddingTop: 16, borderTop: `1px solid ${BORDER}`, fontSize: 12.5, color: "#8a8378" }}>
          {t("aiTransparency.note", {
            defaultValue:
              "Cette page est mise à jour à mesure que les fonctions IA d'ARTKIV évoluent. Règlement (UE) 2024/1689 sur l'intelligence artificielle.",
          })}
        </p>
      </div>
    </div>
  );
}
